import { useEffect, useState } from 'react';
import { SaveIcon, CheckCircle2Icon, AlertCircleIcon } from 'lucide-react';
import type { WelcomePanelResponse, WelcomePanelTenant } from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { RichTextEditor } from '../components/RichTextEditor.js';
import { ApiError, apiGet, apiPatch } from '../api/client.js';
import { vergeetWelcomePanel } from '../components/WelcomePanel.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Beheer ⟶ Welkomstpaneel — Linda's editor for the platform-wide
 * welcome / disclaimer text.
 *
 * Single markdown textarea + live preview. Save bumps the doc's
 * version on the backend, which auto-fires the modal again for every
 * user on their next page load. RBAC enforced server-side; the
 * `/beheer/welkomstpaneel` route is gated by RoleGuard at the App
 * level so a non-admin never reaches this component.
 */
const OMGEVINGEN: Record<WelcomePanelTenant, string> = {
  suppohrt: 'SUPPOHRT',
  rhogo: 'RHOGO',
};

export function BeheerWelkomstpaneelPage(): JSX.Element {
  const { user } = useAuth();
  // Alleen suppohrt_admin kiest een omgeving; een RHOGO-beheerder (ook wie via
  // de wisselknop in RHOGO zit) bewerkt het paneel van de eigen tenant.
  const kiestOmgeving = user?.role === 'suppohrt_admin';
  const [content, setContent] = useState('');
  const [contentZonderAi, setContentZonderAi] = useState('');
  const [tab, setTab] = useState<'ai' | 'zonderAi'>('ai');
  // RHOGO heeft een eigen paneel met een eigen versie (besluit 26 okt 2026).
  const [omgeving, setOmgeving] = useState<WelcomePanelTenant>(
    kiestOmgeving || user?.tenantId !== 'rhogo' ? 'suppohrt' : 'rhogo',
  );
  const [serverVersion, setServerVersion] = useState(0);
  const [updatedAt, setUpdatedAt] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setErrorMsg(null);
    setSavedToast(null);
    // Snel wisselen tussen omgevingen: een trage eerste lading mag de tekst
    // van de tweede niet overschrijven.
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiGet<WelcomePanelResponse>(`/api/v1/welcome-panel?tenant=${omgeving}`);
        if (cancelled) return;
        setContent(resp.content);
        setContentZonderAi(resp.contentZonderAi);
        vergeetWelcomePanel();
        setServerVersion(resp.version);
        setUpdatedAt(resp.updatedAt);
        setUpdatedBy(resp.updatedBy);
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(
          err instanceof ApiError
            ? `Laden mislukt (${err.status}): ${err.message}`
            : 'Welkomstpaneel kon niet geladen worden.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [omgeving]);

  async function save() {
    setSaving(true);
    setErrorMsg(null);
    setSavedToast(null);
    try {
      const resp = await apiPatch<{
        content: string;
        version: number;
        updatedAt: string;
        updatedBy: string;
      }>(`/api/v1/welcome-panel?tenant=${omgeving}`, { content, contentZonderAi });
      setServerVersion(resp.version);
      setUpdatedAt(resp.updatedAt);
      setUpdatedBy(resp.updatedBy);
      setSavedToast(
        `Opgeslagen als versie ${resp.version}. Iedere gebruiker van ${OMGEVINGEN[omgeving]} ziet de modal opnieuw bij het volgende bezoek.`,
      );
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError
          ? `Opslaan mislukt (${err.status}): ${err.message}`
          : 'Opslaan mislukt — probeer het later opnieuw.',
      );
    } finally {
      setSaving(false);
    }
  }

  // Eén versie voor beide teksten: ze worden samen opgeslagen en samen
  // opnieuw getoond, dus een aparte teller per tab zou suggereren dat je ze
  // los kunt publiceren.
  const huidigeTekst = tab === 'ai' ? content : contentZonderAi;
  const overLimit = content.length > 16000 || contentZonderAi.length > 16000;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-6xl px-4 py-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-ink-900">Beheer Welkomstpaneel</h1>
          <p className="mt-1 text-sm text-ink-900/65">
            Schrijf de welkomsttekst, instructies en disclaimer die elke gebruiker te zien krijgt.
            Er zijn twee versies: één voor accounts waar Mila en Sara aan staan en één voor de rest.
            Beide worden samen opgeslagen en verschijnen als modal op het volgende bezoek van elke
            gebruiker. Selecteer tekst voor vet, cursief, lijsten en links.
          </p>
        </header>

        {kiestOmgeving && (
          <div className="mb-4 flex items-center gap-2 text-sm">
            <span className="text-ink-900/65">Kennishub van</span>
            {(Object.keys(OMGEVINGEN) as WelcomePanelTenant[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setOmgeving(key)}
                disabled={saving}
                className={
                  omgeving === key
                    ? 'rounded-full bg-brand-700 px-4 py-1.5 font-semibold text-white'
                    : 'rounded-full border border-ring2 px-4 py-1.5 text-ink-900/70 hover:border-brand-700'
                }
              >
                {OMGEVINGEN[key]}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-ring2 bg-white px-6 py-10 text-center text-sm text-ink-900/55">
            Laden…
          </div>
        ) : (
          /* De aparte voorbeeld-kolom is weg: de editor toont de tekst nu zoals de
             gebruiker hem straks ziet, dus ernaast hetzelfde nog eens tonen is ruis. */
          <div className="flex flex-col">
            <div className="mb-3 flex gap-2">
              {(
                [
                  ['ai', 'Met Mila en Sara'],
                  ['zonderAi', 'Zonder Mila en Sara'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={
                    tab === key
                      ? 'rounded-full bg-brand-700 px-4 py-1.5 text-sm font-semibold text-white'
                      : 'rounded-full border border-ring2 px-4 py-1.5 text-sm text-ink-900/70 hover:border-brand-700'
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="rounded-2xl border border-ring2 bg-white p-4 shadow-card">
              {tab === 'ai' ? (
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Welkom bij de Kennishub…"
                  minHeight="min-h-[460px]"
                />
              ) : (
                <RichTextEditor
                  value={contentZonderAi}
                  onChange={setContentZonderAi}
                  placeholder="Laat leeg om dezelfde tekst te tonen als hierboven…"
                  minHeight="min-h-[460px]"
                />
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className={huidigeTekst.length > 16000 ? 'text-red-600' : 'text-ink-900/55'}>
                {huidigeTekst.length} / 16000 tekens
              </span>
              <span className="text-ink-900/55">
                Versie {serverVersion}
                {updatedBy && updatedAt && updatedBy !== 'system'
                  ? ` · laatst bewerkt door ${updatedBy} op ${new Date(updatedAt).toLocaleString('nl-NL')}`
                  : ''}
              </span>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          {savedToast && (
            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
              <CheckCircle2Icon size={14} /> {savedToast}
            </span>
          )}
          {errorMsg && (
            <span className="inline-flex items-center gap-1.5 text-sm text-red-700">
              <AlertCircleIcon size={14} /> {errorMsg}
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || loading || overLimit}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-5 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
          >
            <SaveIcon size={14} />
            {saving ? 'Opslaan…' : 'Opslaan & publiceren'}
          </button>
        </div>
      </div>
    </Layout>
  );
}
